<template>
    <!-- The report's site-wide defaults: what it contains and how often it is
         sent when no group report applies, and who receives that send. -->
    <slide-over-panel   :show="show"
                        @update:show="$emit('update:show', $event)"
    >
        <template #title>
            Default Report Settings
        </template>

        <template #content>
            <div class="flex flex-col gap-6 pb-8">
                <p class="m-0 text-sm leading-6 text-gray-600">
                    Used whenever this report is sent without a group report.
                </p>

                <alert-toast    v-model:show="showError"
                                type="error"
                                title="Error"
                                :duration="0"
                >{{ errorText }}</alert-toast>

                <report-settings-panel  v-model="settings"
                                        v-model:interval="interval"
                                        :schema="split.settingsSchema"
                                        :is-savable="false"
                                        :timezone="timezone"
                                        :context="context"
                                        variant="cards"
                ></report-settings-panel>

                <report-card    v-if="split.detailFields.length > 0"
                                title="Send Frequency"
                >
                    <report-settings-panel  v-model="settings"
                                            v-model:interval="interval"
                                            :schema="{sections: [{title: 'Send Frequency', fields: split.detailFields}]}"
                                            :is-savable="true"
                                            :timezone="timezone"
                                            :context="context"
                                            variant="bare"
                    ></report-settings-panel>
                </report-card>

                <user-select-input  v-model="settings.subscribedUsers"
                                    :users="users"
                                    label="Recipients (shared)"
                                    modal-title="Choose recipients"
                                    modal-description="They will be emailed this report each time it runs on schedule. This list is shared by everyone on this site."
                                    choose-label="Choose recipients"
                                    change-label="Change recipients"
                                    empty-summary="No users selected"
                                    empty-message="No users are available to receive this report."
                                    noun="user"
                                    plural-noun="users"
                ></user-select-input>

                <div class="flex justify-end">
                    <button v-if="canEdit"
                            type="button"
                            :disabled="saving || ! loaded"
                            class="inline-flex items-center gap-2 rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium leading-5 text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                            @click="save"
                    >
                        <tabler-icon    v-if="saving"
                                        name="loader"
                                        size-class="h-4 w-4 animate-spin"
                        ></tabler-icon>
                        Save
                    </button>
                </div>
            </div>
        </template>
    </slide-over-panel>
</template>

<script>
import _ from "lodash"

import AlertToast from "@/components/elements/AlertToast.vue"
import SlideOverPanel from "@/components/elements/SlideOverPanel.vue"
import TablerIcon from "@/components/elements/TablerIcon.vue"
import UserSelectInput from "@/components/elements/UserSelectInput.vue"
import ReportCard from "./ReportCard.vue"
import ReportSettingsPanel from "./ReportSettingsPanel.vue"
import {splitTemplateSchema} from "./templateSchema.js"

export default {
    name: "DefaultSettingsPanel",
    components: {AlertToast, SlideOverPanel, TablerIcon, UserSelectInput, ReportCard, ReportSettingsPanel},
    emits: ['update:show', 'saved'],
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        schema: {
            type: Object,
            required: true,
        },
        // useReportTemplates() for this report.
        templatesApi: {
            type: Object,
            required: true,
        },
        // The report's own defaults, under the stored default settings.
        defaultSettings: {
            type: Object,
            default: () => ({}),
        },
        prepareSettings: {
            type: Function,
            default: null,
        },
        // {userId: name} of users who can receive the report.
        users: {
            type: [Object, Array],
            default: () => ({}),
        },
        timezone: {
            type: String,
            default: null,
        },
        context: {
            type: Object,
            default: () => ({}),
        },
        canEdit: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            settings: {subscribedUsers: []},
            interval: 'monthly',
            saving: false,
            showError: false,
            errorText: '',
        }
    },
    computed: {
        // Until the saved defaults arrive, saving would write fallbacks over
        // the site's real defaults.
        loaded() {
            return this.templatesApi.loaded.value === true
        },
        split() {
            return splitTemplateSchema(this.schema)
        },
    },
    created() {
        // Opened before the list request finished: pick up the saved defaults
        // when they arrive.
        this.$watch(() => this.templatesApi.defaultTemplate.value, () => {
            if (this.show) {
                this.reset()
            }
        })
    },
    watch: {
        // Each open starts from what is saved, so a cancelled edit is dropped.
        show: {
            handler(open) {
                if (open) {
                    this.reset()
                }
            },
            immediate: true,
        },
    },
    methods: {
        reset() {
            const saved = this.templatesApi.defaultTemplate.value
            this.settings = {
                ..._.cloneDeep(this.defaultSettings),
                ..._.cloneDeep(saved?.settings ?? {}),
            }
            if (this.prepareSettings) {
                this.settings = this.prepareSettings(this.settings) ?? this.settings
            }
            this.settings.subscribedUsers = this.settings.subscribedUsers ?? []
            this.interval = saved?.interval ?? 'monthly'
            this.showError = false
        },
        save() {
            if (this.saving || ! this.loaded) {
                return
            }
            this.saving = true

            let settings = _.cloneDeep(this.settings)
            if (this.prepareSettings) {
                settings = this.prepareSettings(settings) ?? settings
            }

            this.templatesApi.saveDefault({interval: this.interval, settings})
                .then(() => {
                    this.$emit('saved')
                    this.$emit('update:show', false)
                })
                .catch((error) => {
                    this.errorText = this.templatesApi.errorMessages(error).join(' ')
                    this.showError = true
                })
                .finally(() => {
                    this.saving = false
                })
        },
    },
}
</script>
